// Position / range conversion between CodeMirror's flat offset model and
// LSP's `{ line, character }` model (0-based, UTF-16 code units).
//
// CodeMirror's `Text` document is also UTF-16 indexed, so within a line
// the `character` field equals `pos - line.from`. No surrogate-pair
// gymnastics are required for the languages we currently target — the
// Nowline DSL is ASCII, but identifiers and string contents may include
// any BMP character. The math here would need revisiting only if the
// LSP server ever negotiates UTF-32 / UTF-8 position encoding (it
// doesn't today).

import type { Text } from '@codemirror/state';
import type { Range as LspRange, Position } from 'vscode-languageserver-protocol';

export function cmPosToLspPosition(doc: Text, pos: number): Position {
    const line = doc.lineAt(pos);
    return { line: line.number - 1, character: pos - line.from };
}

export function lspPositionToCmPos(doc: Text, position: Position): number {
    const safeLine = clamp(position.line + 1, 1, doc.lines);
    const line = doc.line(safeLine);
    const offset = Math.max(0, position.character);
    return Math.min(line.from + offset, line.to);
}

export function lspRangeToCm(doc: Text, range: LspRange): { from: number; to: number } {
    const from = lspPositionToCmPos(doc, range.start);
    const to = lspPositionToCmPos(doc, range.end);
    return from <= to ? { from, to } : { from: to, to: from };
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}
